import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent } from "@repo/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@repo/ui/sheet";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { openStoredPage, type PropertySnapshot } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";

const columns = [
  "listing_type",
  "sale_date",
  "sale_price",
  "asking_price",
  "price_display",
  "property_type",
  "bedrooms",
  "bathrooms",
  "parking",
  "property_size",
  "land_size",
  "agency_name",
] as const;
const display = (value: unknown) =>
  value == null ? "—" : typeof value === "object" ? JSON.stringify(value) : String(value);
const rawValue = (raw: Record<string, unknown> | null, field: string) => {
  const camel = field.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
  return raw?.[field] ?? raw?.[camel];
};

export function PropertyEvidence({ snapshots }: { snapshots: PropertySnapshot[] }) {
  const [selected, setSelected] = useState<PropertySnapshot | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  async function openStored(snapshot: PropertySnapshot) {
    if (!snapshot.page_snapshot) return;
    setOpening(snapshot.id);
    try {
      await openStoredPage(snapshot.page_snapshot);
    } catch (error) {
      toast({
        title: "Stored page unavailable",
        description: error instanceof Error ? error.message : "Could not load stored page",
        variant: "destructive",
      });
    } finally {
      setOpening(null);
    }
  }
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <h2 className="font-semibold">Crawls and evidence ({snapshots.length})</h2>
        {snapshots.length === 0 && (
          <p className="text-sm text-muted-foreground">No captured snapshots yet.</p>
        )}
        <div className="space-y-2">
          {snapshots.map((snapshot) => (
            <div key={snapshot.id} className="rounded border p-3 text-sm space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">
                  {formatDate(snapshot.page_snapshot?.captured_at ?? snapshot.snapshot_date, {
                    includeTime: true,
                  })}
                </span>
                <Badge variant="outline">{snapshot.adapter_slug ?? "Unknown adapter"}</Badge>
                <Badge variant="outline">
                  {snapshot.page_snapshot?.provider ?? "Unknown provider"}
                </Badge>
                {snapshot.listing_type && (
                  <Badge variant="secondary">{snapshot.listing_type}</Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-3 text-xs">
                <a
                  className="text-primary hover:underline break-all"
                  href={snapshot.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source URL
                </a>
                {snapshot.page_snapshot ? (
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    disabled={opening === snapshot.id}
                    onClick={() => openStored(snapshot)}
                  >
                    {opening === snapshot.id ? "Opening…" : "Open stored page"}
                  </button>
                ) : (
                  <span className="text-muted-foreground">No stored page</span>
                )}
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => setSelected(snapshot)}
                >
                  View parsed fields
                </button>
              </div>
            </div>
          ))}
        </div>
        <Sheet
          open={!!selected}
          onOpenChange={(open) => {
            if (!open) setSelected(null);
          }}
        >
          <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Parsed fields and raw evidence</SheetTitle>
            </SheetHeader>
            {selected && (
              <div className="mt-4 space-y-4 text-xs">
                <p className="text-muted-foreground">
                  Saved columns versus same-named raw JSON fields. Other raw fields remain below.
                </p>
                <div className="space-y-1">
                  <div className="grid grid-cols-3 gap-2 font-semibold">
                    <span>Field</span>
                    <span>Saved column</span>
                    <span>Raw field</span>
                  </div>
                  {columns.map((field) => (
                    <div key={field} className="grid grid-cols-3 gap-2 border-b py-1">
                      <span>{field}</span>
                      <span className="break-all font-mono">{display(selected[field])}</span>
                      <span className="break-all font-mono">
                        {display(rawValue(selected.raw_data, field))}
                      </span>
                    </div>
                  ))}
                </div>
                <h3 className="font-semibold">Raw scraper JSON</h3>
                <pre className="rounded bg-muted p-3 overflow-x-auto">
                  {JSON.stringify(selected.raw_data ?? {}, null, 2)}
                </pre>
                {selected.page_snapshot && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openStored(selected)}
                  >
                    Open stored page
                  </Button>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>
      </CardContent>
    </Card>
  );
}
